import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import type { Business } from '@/types';
import { useAuth } from './AuthContext';

// ===========================================
// Types
// ===========================================

// BusinessView allows 'both' for reading/filtering data
export type BusinessView = Business | 'both';

interface BusinessContextValue {
  // For writing data - must be specific business
  currentBusiness: Business;
  setCurrentBusiness: (business: Business) => void;
  toggleBusiness: () => void;

  // For reading/filtering data - can be 'both'
  currentBusinessView: BusinessView;
  setCurrentBusinessView: (view: BusinessView) => void;

  businesses: Business[];
  getBusinessLabel: (business: Business) => string;
  getBusinessViewLabel: (view: BusinessView) => string;
  getBusinessColor: (business: Business) => string;
}

// ===========================================
// Constants
// ===========================================

const BUSINESSES: Business[] = ['azdisaster', 'blacksmith'];

const BUSINESS_LABELS: Record<Business, string> = {
  azdisaster: 'ACR',
  blacksmith: 'Blacksmith',
};

const BUSINESS_VIEW_LABELS: Record<BusinessView, string> = {
  both: 'Both (ACR & Blacksmith)',
  azdisaster: 'ACR',
  blacksmith: 'Blacksmith',
};

const BUSINESS_COLORS: Record<Business, { bg: string; text: string; border: string }> = {
  azdisaster: {
    bg: 'bg-red-600',
    text: 'text-red-600',
    border: 'border-red-600',
  },
  blacksmith: {
    bg: 'bg-slate-800',
    text: 'text-slate-800',
    border: 'border-slate-800',
  },
};

// ===========================================
// Context
// ===========================================

const BusinessContext = createContext<BusinessContextValue | null>(null);

// ===========================================
// Provider Component
// ===========================================

export function BusinessProvider({ children }: { children: React.ReactNode }) {
  const { user, updatePreferredBusiness } = useAuth();
  const [currentBusiness, setCurrentBusinessState] = useState<Business>('azdisaster');
  const [currentBusinessView, setCurrentBusinessViewState] = useState<BusinessView>('azdisaster');

  // Initialize from user preference
  useEffect(() => {
    if (user?.preferredBusiness) {
      setCurrentBusinessState(user.preferredBusiness);
      setCurrentBusinessViewState(user.preferredBusiness);
    }
  }, [user?.preferredBusiness]);

  // Set current business and persist to user preferences
  const setCurrentBusiness = useCallback(
    async (business: Business) => {
      setCurrentBusinessState(business);
      // Also update view to match
      setCurrentBusinessViewState(business);

      // Persist preference if user is logged in
      if (user) {
        try {
          await updatePreferredBusiness(business);
        } catch (error) {
          console.error('Failed to persist business preference:', error);
          // Don't revert the local state - still allow user to work
        }
      }
    },
    [user, updatePreferredBusiness]
  );

  // Set business view (for filtering/reading data) - doesn't persist
  const setCurrentBusinessView = useCallback((view: BusinessView) => {
    setCurrentBusinessViewState(view);
    // If selecting a specific business, also update the write business
    if (view !== 'both') {
      setCurrentBusinessState(view);
    }
  }, []);

  // Toggle between businesses
  const toggleBusiness = useCallback(() => {
    const newBusiness = currentBusiness === 'azdisaster' ? 'blacksmith' : 'azdisaster';
    setCurrentBusiness(newBusiness);
  }, [currentBusiness, setCurrentBusiness]);

  // Get display label for a business
  const getBusinessLabel = useCallback((business: Business) => {
    return BUSINESS_LABELS[business];
  }, []);

  // Get display label for a business view (includes 'both')
  const getBusinessViewLabel = useCallback((view: BusinessView) => {
    return BUSINESS_VIEW_LABELS[view];
  }, []);

  // Get color classes for a business
  const getBusinessColor = useCallback((business: Business) => {
    return BUSINESS_COLORS[business].bg;
  }, []);

  const value: BusinessContextValue = {
    currentBusiness,
    setCurrentBusiness,
    toggleBusiness,
    currentBusinessView,
    setCurrentBusinessView,
    businesses: BUSINESSES,
    getBusinessLabel,
    getBusinessViewLabel,
    getBusinessColor,
  };

  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
}

// ===========================================
// Hook
// ===========================================

export function useBusiness() {
  const context = useContext(BusinessContext);

  if (!context) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }

  return context;
}

// ===========================================
// Business Toggle Component (original)
// ===========================================

export function BusinessToggle({ className = '' }: { className?: string }) {
  const { currentBusiness, toggleBusiness, getBusinessLabel } = useBusiness();

  const isAzDisaster = currentBusiness === 'azdisaster';

  return (
    <button
      onClick={toggleBusiness}
      className={`
        relative inline-flex h-10 items-center rounded-full p-1 w-48 transition-colors
        ${isAzDisaster ? 'bg-red-600' : 'bg-slate-800'}
        ${className}
      `}
      aria-label={`Switch to ${isAzDisaster ? 'Blacksmith' : 'ACR'}`}
    >
      {/* Sliding indicator */}
      <span
        className={`
          absolute h-8 w-[calc(50%-4px)] rounded-full bg-white shadow-md
          transition-transform duration-200 ease-in-out
          ${isAzDisaster ? 'translate-x-0' : 'translate-x-[calc(100%+4px)]'}
        `}
      />

      {/* Labels */}
      <span
        className={`
          relative z-10 flex-1 text-center text-sm font-medium transition-colors
          ${isAzDisaster ? 'text-red-600' : 'text-white'}
        `}
      >
        {getBusinessLabel('azdisaster')}
      </span>
      <span
        className={`
          relative z-10 flex-1 text-center text-sm font-medium transition-colors
          ${isAzDisaster ? 'text-white' : 'text-slate-800'}
        `}
      >
        {getBusinessLabel('blacksmith')}
      </span>
    </button>
  );
}

// ===========================================
// Business Selector Component (dropdown with 'both' option)
// ===========================================

export function BusinessSelector({ className = '' }: { className?: string }) {
  const { currentBusinessView, setCurrentBusinessView, getBusinessViewLabel } = useBusiness();
  const [isOpen, setIsOpen] = useState(false);

  const options: BusinessView[] = ['both', 'azdisaster', 'blacksmith'];

  const getButtonColor = () => {
    switch (currentBusinessView) {
      case 'azdisaster':
        return 'bg-red-600 text-white';
      case 'blacksmith':
        return 'bg-slate-800 text-white';
      case 'both':
        return 'bg-gradient-to-r from-red-600 to-slate-800 text-white';
      default:
        return 'bg-gray-600 text-white';
    }
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center justify-between gap-2 px-4 py-2 rounded-lg
          min-w-[200px] font-medium transition-colors
          ${getButtonColor()}
        `}
      >
        <span>{getBusinessViewLabel(currentBusinessView)}</span>
        <ChevronDown size={18} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full left-0 mt-1 w-full bg-white rounded-lg shadow-lg border z-50 overflow-hidden">
            {options.map((option) => (
              <button
                key={option}
                onClick={() => {
                  setCurrentBusinessView(option);
                  setIsOpen(false);
                }}
                className={`
                  w-full px-4 py-2.5 text-left text-sm transition-colors
                  ${currentBusinessView === option
                    ? 'bg-blue-50 text-blue-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                  }
                `}
              >
                {getBusinessViewLabel(option)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
